import React, { useState } from 'react'
import { useT } from '../../lib/i18n'
import { useQuery } from '../../lib/api'

interface Commit {
  hash: string; shortHash: string; date: string
  message: string; author: string; refs: string
}
interface DiffLine {
  type: 'added' | 'removed' | 'context'
  content: string; lineOld?: number; lineNew?: number
}
interface DiffHunk { header: string; lines: DiffLine[] }
interface FileDiff { file: string; hunks: DiffHunk[] }

const PROJECT_IDS = [
  'erp_kaytech', 'kaytechnologie', 'erp_assg',
  'erp_supmanagement', 'tontine_app', 'erp_agasa', 'cumcco_ga',
]

export default function GitDiff() {
  const t = useT()
  const [selectedProject, setSelectedProject] = useState(PROJECT_IDS[0]!)
  const [selectedSha, setSelectedSha] = useState<string | null>(null)

  const { data: commitsData, loading: commitsLoading } =
    useQuery<{ commits: Commit[] }>(`/projects/${selectedProject}/git/commits?limit=20`)

  const { data: diffData, loading: diffLoading } = useQuery<{ files: FileDiff[] }>(
    selectedSha ? `/projects/${selectedProject}/git/diff/${selectedSha}` : ''
  )

  return (
    <div style={{ display: 'flex', gap: '0', height: '100%', overflow: 'hidden' }}>
      {/* Panneau gauche — liste commits */}
      <div style={{ width: '280px', flexShrink: 0, borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <select
            value={selectedProject}
            onChange={(e) => { setSelectedProject(e.target.value); setSelectedSha(null) }}
            style={{ padding: '5px 8px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--surface)', color: 'var(--text)', fontFamily: 'var(--font-sans)', fontSize: '12px' }}
          >
            {PROJECT_IDS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {commitsLoading ? (
            <div style={{ padding: '16px', fontSize: '12px', color: 'var(--text-muted)' }}>{t('loading')}</div>
          ) : (commitsData?.commits ?? []).length === 0 ? (
            <div style={{ padding: '16px', fontSize: '12px', color: 'var(--text-muted)' }}>
              Aucun commit trouvé — dépôt Git non détecté
            </div>
          ) : (
            (commitsData?.commits ?? []).map((c) => (
              <button
                key={c.hash}
                onClick={() => setSelectedSha(c.hash)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '8px 12px',
                  border: 'none',
                  borderBottom: '1px solid var(--border)',
                  background: selectedSha === c.hash ? 'var(--accent-dim)' : 'transparent',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-sans)',
                }}
              >
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '2px' }}>
                  <code style={{ fontSize: '10px', color: 'var(--accent)', fontFamily: 'var(--font-mono)', background: 'var(--accent-dim)', padding: '1px 5px', borderRadius: '3px' }}>
                    {c.shortHash}
                  </code>
                  {c.refs && (
                    <span style={{ fontSize: '10px', color: 'var(--health-good)', fontFamily: 'var(--font-mono)' }}>
                      {c.refs.split(',')[0]?.trim()}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text)', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.message}
                </div>
                <div style={{ fontSize: '10px', color: 'var(--text-dim)', marginTop: '2px', fontFamily: 'var(--font-mono)' }}>
                  {c.author} · {c.date.slice(0, 10)}
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Panneau droit — diff */}
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, minWidth: 0 }}>
        {!selectedSha ? (
          <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>
            ← Sélectionnez un commit pour voir le diff
          </div>
        ) : diffLoading ? (
          <div style={{ padding: '16px', fontSize: '12px', color: 'var(--text-muted)' }}>{t('loading')}</div>
        ) : (diffData?.files ?? []).length === 0 ? (
          <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>
            Diff vide ou non disponible
          </div>
        ) : (
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: '12px' }}>
            {(diffData?.files ?? []).map((f, fi) => (
              <div key={fi} style={{ marginBottom: '16px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
                <div style={{ padding: '6px 12px', background: 'var(--surface-2)', borderBottom: '1px solid var(--border)', fontWeight: 600, color: 'var(--text)' }}>
                  {f.file}
                </div>
                {f.hunks.map((h, hi) => (
                  <div key={hi}>
                    <div style={{ padding: '4px 12px', background: 'var(--surface)', color: 'var(--text-dim)', borderBottom: '1px solid var(--border)', fontSize: '11px' }}>
                      {h.header}
                    </div>
                    {h.lines.map((line, li) => (
                      <div
                        key={li}
                        style={{
                          display: 'flex',
                          background: line.type === 'added' ? 'rgba(34,197,94,.1)' : line.type === 'removed' ? 'rgba(239,68,68,.1)' : 'transparent',
                          borderLeft: `3px solid ${line.type === 'added' ? 'var(--health-good)' : line.type === 'removed' ? 'var(--health-crit)' : 'transparent'}`,
                        }}
                      >
                        <span style={{ width: '36px', textAlign: 'right', padding: '1px 8px', color: 'var(--text-dim)', flexShrink: 0, userSelect: 'none', borderRight: '1px solid var(--border)' }}>
                          {line.type === 'removed' ? (line.lineOld ?? '') : (line.lineNew ?? '')}
                        </span>
                        <span style={{ padding: '1px 8px', color: line.type === 'added' ? 'var(--health-good)' : line.type === 'removed' ? 'var(--health-crit)' : 'var(--text)', flex: 1, whiteSpace: 'pre' }}>
                          {line.type === 'added' ? '+' : line.type === 'removed' ? '-' : ' '}{line.content}
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
